import {css, StyleSheet} from "aphrodite";
import gql from "graphql-tag";
import React, {useState} from "react";

import {useOnMountEffect} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {Errors} from "@khanacademy/wonder-stuff-core";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {useMutation, useQuery} from "@khan/data-gql";
import {gqlOp} from "@khan/data-gql-core";
import Log from "@khan/logging";
import {PermissionsInfo, RequestInfo} from "@khan/shared-core";
import {preserveMarketingParams, useLocation} from "@khan/urls";

import PageRoot from "#app-shell-package/page-root.tsx";
import {TEACHER_KHANMIGO_TOOLS_PATH} from "#coach-dashboard-package/khanmigo/path-helpers.ts";
import useProfile from "#data-access/use-profile.ts";
import preserveReferralParams from "#khanmigo-for-teachers-referrals-package/preserve-referral-params.ts";
import type {ProfileModelType} from "#shared-package/profile-model.ts";
import SmartRedirect from "#shared/app-shell/smart-redirect.tsx";

import type {
    freeForTeachersEnroll,
    freeForTeachersEnrollVariables,
} from "./__graphql-types__/freeForTeachersEnroll.ts";
import type {
    freeForTeachersEnrollEligibility,
    freeForTeachersEnrollEligibilityVariables,
} from "./__graphql-types__/freeForTeachersEnrollEligibility.ts";
import FreeForTeachersEligibilityForm from "./free-for-teachers-eligibility-form.tsx";
import AlreadyEnrolledFreeForTeachers from "./join-wizard/already-enrolled-free-for-teachers.tsx";
import CannotDetermineEligibility from "./join-wizard/cannot-determine-eligibility.tsx";
import EnglishOnly from "./join-wizard/en-only.tsx";
import EnrollmentFailed from "./join-wizard/enrollment-failed.tsx";
import IneligibleCountry from "./join-wizard/ineligible-country.tsx";
import KnownStudent from "./join-wizard/known-student.tsx";
import UnderAgeGate from "./join-wizard/under-age-gate.tsx";
import {isCountryBlocked} from "./khanmigo-allowed-countries.ts";
import {KhanmigoHeader} from "./khanmigo-header.tsx";
import {withStrings} from "./locales/free-for-teachers-enroll/load-strings.tsx";
import useFreeForTeachersTerms from "./use-free-for-teachers-terms.ts";
import {useMakeFreeForTeachersReferral} from "./use-make-free-for-teachers-referral.ts";

function FreeForTeachersEnroll() {
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const {remoteCountry, kaLocale} = RequestInfo;
    const {search} = useLocation();

    const profileResult = useProfile();
    const profile: ProfileModelType | null =
        profileResult.status === "success" && profileResult.profile
            ? profileResult.profile
            : null;

    const isEligibleToBeReferred =
        remoteCountry.toLowerCase() === "us" &&
        !profile?.get("includesDistrictOwnedData");

    useOnMountEffect(() => {
        publishAnalyticsEvent({
            eventType: "AiGuideTeacherFeaturesEnrollPageViewed",
            eventSchemaVersion: 2,
            countryCode: remoteCountry.toLowerCase(),
        });
    });

    const SUPPORTED_LOCALES = {
        en: true,
        es: true,
        pt: true,
        hi: true,
    };

    return (
        <PageRoot
            settings={{
                header: false,
                footer: false,
                noIndex: true,
                noFollow: true,
                // TODO: choose a title
            }}
        >
            {(() => {
                if (isCountryBlocked(remoteCountry)) {
                    return (
                        <EnrollPage>
                            <IneligibleCountry
                                kaLocale={kaLocale}
                                countryCode={remoteCountry}
                                product="teacher-tools"
                            />
                        </EnrollPage>
                    );
                }

                if (!SUPPORTED_LOCALES[kaLocale.toLowerCase()]) {
                    return (
                        <EnrollPage>
                            <EnglishOnly
                                kaLocale={kaLocale}
                                countryCode={remoteCountry}
                            />
                        </EnrollPage>
                    );
                }

                if (!PermissionsInfo.isLoggedIn) {
                    const continueURL = encodeURIComponent(
                        preserveReferralParams(
                            "/khanmigo/teacher-tools",
                            new URLSearchParams(search),
                        ),
                    );

                    const signUpURL = preserveMarketingParams(
                        `/signup?referral=KhanmigoSignup&isteacher=true&continue=${continueURL}`,
                        new URLSearchParams(search),
                    );

                    return <SmartRedirect to={signUpURL} />;
                }

                return (
                    <EnrollPage>
                        <PersonalizedEligibilityStep
                            // this is currently the only restriction against users who are signing up via a referral
                            allowReferrals={isEligibleToBeReferred}
                        />
                    </EnrollPage>
                );
            })()}
        </PageRoot>
    );
}

export default withStrings(FreeForTeachersEnroll);

function EnrollPage(props: {children: React.ReactNode}) {
    return (
        <>
            <KhanmigoHeader />
            {props.children}
        </>
    );
}

export function useTeacherToolsEnrollMutation() {
    return useMutation(FREE_FOR_TEACHERS_ENROLL_MUTATION);
}

function PersonalizedEligibilityStep({
    allowReferrals,
}: {
    allowReferrals: boolean;
}) {
    const {search, queryParams} = useLocation();
    const referralCode = queryParams.referralCode;
    const termsURL = useFreeForTeachersTerms();

    const [agreesToTerms, setAgreesToTerms] = useState(false);

    const [eligibilityResult, refetchEligibility] = useQuery(
        FREE_FOR_TEACHERS_ELIGIBILITY_QUERY,
        {variables: {agreesToTerms}},
    );

    const [enrollUser, enrollUserResult] = useTeacherToolsEnrollMutation();

    const makeFreeForTeachersReferral = useMakeFreeForTeachersReferral();

    // New state to control when we move forward with the redirect
    const [enrollmentCompleted, setEnrollmentCompleted] = useState(false);

    const enroll = async () => {
        const enrollResult = await enrollUser({
            termsUrl: termsURL,
            userAcceptedTerms: agreesToTerms,
            countryCode: RequestInfo.remoteCountry,
        });

        // We only make the referral call if the enrollment was
        // successful with no errors, and we have a referralCode in url
        if (
            allowReferrals &&
            enrollResult.status === "success" &&
            !enrollResult?.data.activateTeacherTools?.error &&
            referralCode
        ) {
            try {
                await makeFreeForTeachersReferral({referralCode});
            } catch (err: any) {
                Log.error(
                    `Error recording Free for Teachers Referral: ${String(
                        err,
                    )}`,
                    Errors.Internal,
                    {cause: err},
                );
            }
        }

        // We only set EnrollmentCompleted to true after we ensure that
        // both calls have been made and completed
        setEnrollmentCompleted(true);
    };

    const isLoading = eligibilityResult.status === "loading";

    if (isLoading) {
        return (
            <div className={css(styles.centeredEnrollFrame)}>
                <CircularSpinner />
            </div>
        );
    }

    if (
        eligibilityResult.status !== "success" ||
        !eligibilityResult.data.user
    ) {
        return <CannotDetermineEligibility />;
    }

    const accessPlans = eligibilityResult.data.user.aiGuideAccessPlans || [];

    const isAlreadyEnrolled = accessPlans.some(
        (ap) => ap.enrollmentGroup === "TEACHERS_FREE" && ap.isActive,
    );

    if (isAlreadyEnrolled) {
        return <AlreadyEnrolledFreeForTeachers />;
    }

    if (
        eligibilityResult.data.user.aiGuideFreeForTeachersEligibility
            ?.kadStudent === "INELIGIBLE"
    ) {
        return <KnownStudent />;
    }

    if (
        eligibilityResult.data.user.aiGuideFreeForTeachersEligibility?.age ===
        "INELIGIBLE"
    ) {
        return <UnderAgeGate age={eligibilityResult.data.user.age || 0} />;
    }

    const shouldTryFixingEligibility =
        eligibilityResult.data.user.aiGuideFreeForTeachersEligibility?.age ===
            "UNKNOWN" ||
        eligibilityResult.data.user.aiGuideFreeForTeachersEligibility
            ?.aiGuideTermsOfService === "INELIGIBLE" ||
        eligibilityResult.data.user.aiGuideFreeForTeachersEligibility
            ?.aiGuideTermsOfService === "UNKNOWN" ||
        eligibilityResult.data.user.aiGuideFreeForTeachersEligibility
            ?.isTeacher === "INELIGIBLE" ||
        eligibilityResult.data.user.aiGuideFreeForTeachersEligibility
            ?.isTeacher === "UNKNOWN";

    if (shouldTryFixingEligibility) {
        const reauthID =
            eligibilityResult.data.user.email ||
            eligibilityResult.data.user.username;

        if (!reauthID) {
            throw new Error("User has neither email nor username");
        }

        const onEligibilityUpdated = (newBirthdate, newTerms) => {
            setAgreesToTerms(newTerms);

            // If terms acceptance changes it will automatically
            // trigger a refetch so we only need to do so explicitly
            // if the user only corrected other criteria
            if (newTerms === agreesToTerms) {
                refetchEligibility();
            }
        };

        return (
            <FreeForTeachersEligibilityForm
                reauthAccountIdentifier={reauthID}
                afterEligibleSave={onEligibilityUpdated}
                birthdate={eligibilityResult.data.user.birthdate || undefined}
            />
        );
    }

    if (!enrollUserResult) {
        enroll();
    }

    if (!enrollmentCompleted) {
        return (
            <div className={css(styles.centeredEnrollFrame)}>
                <CircularSpinner />
            </div>
        );
    }

    if (
        (enrollUserResult && enrollUserResult.status !== "success") ||
        enrollUserResult?.data.activateTeacherTools?.error
    ) {
        return <EnrollmentFailed />;
    }

    const teacherToolsUrl = preserveMarketingParams(
        TEACHER_KHANMIGO_TOOLS_PATH,
        new URLSearchParams(search),
    );

    // We want to force the full page to reload to ensure the details on the
    // user profile are fetched fresh for the logic that occurs on the Khanmigo
    // teacher tools page.
    return <SmartRedirect to={teacherToolsUrl} forceFullPageLoad={true} />;
}

export const FREE_FOR_TEACHERS_ELIGIBILITY_QUERY = gqlOp<
    freeForTeachersEnrollEligibility,
    freeForTeachersEnrollEligibilityVariables
>(gql`
    query freeForTeachersEnrollEligibility($agreesToTerms: Boolean) {
        user {
            id
            aiGuideAccessPlans {
                id
                enrollmentGroup
                isActive
            }
            age
            birthdate
            email
            username
            aiGuideFreeForTeachersEligibility(
                agreesToAIGuideTermsOfService: $agreesToTerms
            ) {
                age
                aiGuideTermsOfService
                isTeacher
                kadStudent
            }
        }
    }
`);

export const FREE_FOR_TEACHERS_ENROLL_MUTATION = gqlOp<
    freeForTeachersEnroll,
    freeForTeachersEnrollVariables
>(gql`
    mutation freeForTeachersEnroll(
        $termsUrl: String!
        $userAcceptedTerms: Boolean!
        $countryCode: String!
    ) {
        activateTeacherTools(
            termsUrl: $termsUrl
            userAcceptedTerms: $userAcceptedTerms
            countryCode: $countryCode
        ) {
            error {
                code
                debugMessage
            }
        }
    }
`);

const styles = StyleSheet.create({
    centeredEnrollFrame: {
        height: "calc(100vh - 74px)",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
});
